import Constants from 'expo-constants';
import { useEffect } from 'react';

import { useAuthContext } from '@fhs/auth/src/hooks';
import { useUserAccountsQuery } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { ISOs } from '@fhs-legacy/frontend/src/utils/form/constants';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { usePaymentMethodStore } from '..';
import {
  useApplePayAvailability,
  useGooglePayAvailability,
} from '../../components/checkout-components/payment-details/action-payment-method-selection/digital-wallet-availability';

/**
 * Prefetch userAccounts on the cart screen into the payment store so checkout can skip the query.
 * Fire-and-forget: runs in the background while the shopper is on cart.
 */
export function usePreloadPaymentMethods() {
  const enableCartService = useFlag('enable-simply-better-cart-service');
  const { user } = useAuthContext();
  const setUserAccountsData = usePaymentMethodStore(state => state.setUserAccountsData);
  const canUseApplePay = useApplePayAvailability();
  const canUseGooglePay = useGooglePayAvailability();

  const feAlpha3CountryCode =
    Constants.expoConfig?.extra?.eas.buildRegion === 'US' ? ISOs.USA : ISOs.CAN;

  // User's default payment method (set on payment methods page)
  const defaultAccountIdentifier =
    user?.details?.defaultAccountIdentifier ?? user?.details?.defaultFdAccountId;

  // Prefetch on cart only; checkout reuses zustand (see use-available-payment-methods).
  const { data: userAccountsData } = useUserAccountsQuery({
    variables: { feCountryCode: feAlpha3CountryCode },
    fetchPolicy: 'network-only',
    skip: !enableCartService || !user,
  });

  useEffect(() => {
    if (user && userAccountsData && enableCartService) {
      setUserAccountsData(userAccountsData, defaultAccountIdentifier, {
        canUseApplePay,
        canUseGooglePay,
      });
    }
  }, [
    user,
    userAccountsData,
    setUserAccountsData,
    enableCartService,
    defaultAccountIdentifier,
    canUseApplePay,
    canUseGooglePay,
  ]);
}
